import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { VenueService } from '../../services/venues/venue.service';
import { Subject, takeUntil } from 'rxjs';
import { VenueModel } from 'src/app/pos-panel/models/venues/venue.model';
import { MenuService } from '../../services/menu/menu.service';
import { WtrCategoryModel } from '../../models/menu/wtr-category-model';
import { MenuModel } from '../../models/menu/menu-model';
import {MenuItemModel} from "../../models/menu/menu-items.model";
import {OrderModel} from "../../models/orders/order.model";
import {OrderItemModel} from "../../models/orders/order-item.model";
import {DialogService} from "primeng/dynamicdialog";
import {AddNoteComponent} from "./add-note/add-note.component";
import {CapacityService} from "../../services/capacity/capacity.service";
import {CurrencyModel} from "../../models/menu/currency-model";
import {OrderService} from "../../services/order/order.service";
import {debounceTime} from "rxjs/operators";

@Component({
  selector: 'app-waiter-table',
  templateUrl: './waiter-table.component.html',
  styleUrls: ['./waiter-table.component.scss']
})
export class WaiterTableComponent implements OnInit {
  //#region Fields
  public unsubscribe: Subject<void> = new Subject<void>();
  public spinner: boolean = false;
  public venueId: string;
  public venueName: string;
  public tableId: string;
  public mostOrderedItems: Array<any> = new Array<any>();
  public filteredMostOrderedItems: Array<any> = new Array<any>();
  public categoriesList: Array<WtrCategoryModel> = new Array<WtrCategoryModel>();
  public filteredSelectedCategoryChildVenueMenuCategories: Array<WtrCategoryModel> = new Array<WtrCategoryModel>();
  public filteredSelectedCategoryVenueMenuItems: Array<any> = new Array<any>();
  public filteredCategoriesList: Array<WtrCategoryModel> = new Array<WtrCategoryModel>();
  public imagePath: string = './assets/images/logo/category.png';
  public selectedCategory: WtrCategoryModel = new WtrCategoryModel();
  public orderItemsQuantity: { [key: string]: OrderItemModel} = {};
  public totalPrice: number = 0.0;
  public totalPriceWithTax: number = 0.0;
  public orderNote: string;
  public orderBillNumber: string;
  public tableName: string;
  public currency: CurrencyModel = new CurrencyModel();
  public searchText: string;
  private searchDebouncer = new Subject<void>();
  private RELOAD_DEBOUNCE_TIME = 1000;


  //#endregion
  //#region Constructor
  constructor(
    public route: ActivatedRoute,
    private router: Router,
    public dialogService: DialogService,
    public translate: TranslateService,
    public messageService: MessageService,
    public venueService: VenueService,
    public menuService: MenuService,
    public capacityService: CapacityService,
    public orderService: OrderService,
  ) {
    this.retrieveOrder();
  }

  ngOnInit(): void {
    this.searchText = "";
    this.searchDebouncer.pipe(
      debounceTime(this.RELOAD_DEBOUNCE_TIME)
    ).subscribe(_ => this.filterResults());

    this.route.params.subscribe(async params => {
      this.tableId = params['id'];
      this.getTableDetails();
      this.getOrderBillNumberForTable();
    });

    this.route.url.subscribe(async params => {
      this.venueId = params[1].path;
      this.getVenueDetails(this.venueId);
      this.getMostOrderedItems(this.venueId);
      this.getCategories(this.venueId);
    });
  }
  //#endregion
  //#region Public Methods
  public getVenueDetails(venueId: string): void {
    this.venueService.getvenuedetails(venueId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response: VenueModel) => {
          this.spinner = false;
          this.venueName = response.name;
        },
        error: errorResponse => {
          this.spinner = false;
          if (errorResponse.error && errorResponse.error.Message) {
            this.messageService.add({ severity: 'error', summary: errorResponse.error.Message, life: 2000 });
          }
          else if (errorResponse.error && !errorResponse.error.errors) {
            this.messageService.add({ severity: 'error', summary: errorResponse.error.title, life: 2000 });
          }
        }
      });
  }

  public getMostOrderedItems(venueId: string){
    this.menuService.getmostordereditems(venueId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response: any) => {
          this.spinner = false;
          this.mostOrderedItems = response;
          this.filteredMostOrderedItems = response;
        },
        error : errorResponse => {
          this.spinner = false;
          if (errorResponse.error && errorResponse.error.Message) {
            this.messageService.add({ severity: 'error', summary: errorResponse.error.Message, life: 2000 });
          }
          else if (errorResponse.error && !errorResponse.error.errors) {
            this.messageService.add({ severity: 'error', summary: errorResponse.error.title, life: 2000 });
          }
        }
      })
  }

  public getCategories(venueId: string){
    this.menuService.viewmenu(venueId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response: MenuModel) => {
          this.spinner = false;
          if(response) {
            this.categoriesList = response.venueMenuCategories;
            this.filteredCategoriesList = this.categoriesList;
            this.currency = response.defaultCurrency;
          }
        },
        error: errorResponse => {
          this.spinner = false;
          if (errorResponse.error && errorResponse.error.Message) {
            this.messageService.add({ severity: 'error', summary: errorResponse.error.Message, life: 2000 });
          }
          else if (errorResponse.error && !errorResponse.error.errors) {
            this.messageService.add({ severity: 'error', summary: errorResponse.error.title, life: 2000 });
          }
        }
      })
  }

  getOrderBillNumberForTable(){
    this.orderService.viewOrderBillForTable(this.tableId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
      this.orderBillNumber = response.billNumber;
    });
  }

  getTableDetails(){
    this.capacityService.viewTableDetails(this.tableId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
      this.tableName = response.name;
    });
  }

  openCategory(category: WtrCategoryModel){
    this.selectedCategory = category;
    this.filteredSelectedCategoryChildVenueMenuCategories = category.childVenueMenuCategories;
    this.filteredSelectedCategoryVenueMenuItems = category.venueMenuItems;
    this.searchText = "";
  }
  addToOrderItems(menuItem : MenuItemModel){
    if (Object.keys(this.orderItemsQuantity).indexOf(menuItem.id) == -1){
      let item = new OrderItemModel();
      item.menuItemId = menuItem.id;
      item.quantity = 1;
      item.venueMenuItemName = menuItem.name;
      item.venueMenuItemPrice = menuItem.price;
      item.venueMenuItemPriceWithTax =  this.calculatePriceWithTax(menuItem.price, menuItem.taxRate.rate);
      this.orderItemsQuantity[menuItem.id] = item;
    }else{
      this.orderItemsQuantity[menuItem.id].quantity += 1;
    }
    this.totalPrice += menuItem.price;
    this.totalPriceWithTax += this.calculatePriceWithTax(menuItem.price, menuItem.taxRate.rate);
  }

  calculatePriceWithTax(basePrice: number, taxRate: number){
    return basePrice + basePrice * taxRate / 100;
  }

  addNote() {
    const ref = this.dialogService.open(AddNoteComponent, {
      data: {
        note: this.orderNote
      },
      header: this.translate.instant('ORDER.ADD_NOTE'),
      height: '350px',
      width: '400px',
      styleClass: "note_dialog_style"
    });
    ref.onClose.subscribe((response: any) => {
      if(response != undefined){
          this.orderNote = response;
      }
    });
  }
  retrieveOrder(){
    let order = (<OrderModel> this.router.getCurrentNavigation()?.extras.state?.order);
    if(order) {
      this.orderNote = order.note;
      this.totalPrice = order.price;
      this.totalPriceWithTax = order.priceWithTax;
      order.items.forEach((item: OrderItemModel) => this.orderItemsQuantity[item.menuItemId] = item);
    };
  }

  reviewOrderBeforeSaving(){
    let order = new OrderModel();
    order.venueId = this.venueId;
    order.capacityTableId = this.tableId;
    order.currencyId = this.currency.id;
    order.note = this.orderNote;
    order.price = this.totalPrice;
    order.priceWithTax = this.totalPriceWithTax;
    order.items = Object.values(this.orderItemsQuantity);

    this.router.navigateByUrl("/waiter/"+ this.venueId + '/wtr/' + this.tableId + '/display-order',{state: {order: order, currency: this.currency, orderBillNumber: this.orderBillNumber}});
  }

  filterResults() {
    if(Object.keys(this.selectedCategory).length !== 0) {
      this.filteredSelectedCategoryVenueMenuItems = this.selectedCategory.venueMenuItems.filter(x => x.name.toLowerCase().includes(this.searchText.toLowerCase()));
      this.filteredSelectedCategoryChildVenueMenuCategories = this.selectedCategory.childVenueMenuCategories.filter(x => x.name.toLowerCase().includes(this.searchText.toLowerCase()));
    }else {
      this.filteredCategoriesList = this.categoriesList.filter(x => x.name.toLowerCase().includes(this.searchText.toLowerCase()));
    }
  }
  displayBill() {
    this.router.navigateByUrl("/waiter/" + this.venueId + '/wtr/' + this.tableId + '/display-bills');
  }

  goBack(){
    this.router.navigate(['/waiter', this.venueId, 'wtr']);
  }
  goBackToCategories(){
    this.selectedCategory = new WtrCategoryModel();
    this.filteredSelectedCategoryVenueMenuItems = new Array<any>();
    this.filteredSelectedCategoryChildVenueMenuCategories = new Array<WtrCategoryModel>();
    this.searchText = "";
  }
  //#endregion

}
